import React, { useState, useMemo, useEffect, useRef } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../../types/kundali';
import {
  calculateRelocationEventSignature,
  RelocationEventSignature,
  DirectionReferencePoint,
  LocationStatus,
  RelocationTimeHorizon
} from '../../utils/v600/relocationGeographyEngine';
import {
  searchCanonicalLocations,
  VerifiedLocation,
  CANONICAL_CITY_PRESETS
} from '../../services/geoService';
import {
  Globe,
  Compass,
  Navigation,
  MapPin,
  Plane,
  Home,
  Briefcase,
  Layers,
  Calendar,
  Clock,
  Shield,
  CheckCircle2,
  AlertTriangle,
  Info,
  BookOpen,
  Send,
  RotateCcw,
  Sliders,
  Check,
  Building2,
  Database,
  HelpCircle,
  Search,
  X,
  Loader2,
  Edit2
} from 'lucide-react';

interface RelocationGeographyViewProps {
  kundali: CompleteKundali;
  lang?: Language;
  chartStyle?: ChartStyle;
  focusDate?: Date | string;
  initialCurrentResidence?: string | null;
  onOpenConsultation?: (prompt?: string) => void;
}

export const RelocationGeographyView: React.FC<RelocationGeographyViewProps> = ({
  kundali,
  lang = 'en',
  focusDate,
  initialCurrentResidence = null,
  onOpenConsultation
}) => {
  const isHi = lang === 'hi';

  // Independent Location States (RG-HF-V6.0 Section 1-3)
  const STORAGE_KEY_RESIDENCE = 'vedang_current_residence';
  const STORAGE_KEY_STATUS = 'vedang_current_residence_status';

  const [confirmedCurrentResidence, setConfirmedCurrentResidence] = useState<string | null>(() => {
    if (initialCurrentResidence) return initialCurrentResidence;
    try {
      return localStorage.getItem(STORAGE_KEY_RESIDENCE) || null;
    } catch {
      return null;
    }
  });
  const [currentResidenceStatus, setCurrentResidenceStatus] = useState<LocationStatus>(() => {
    if (initialCurrentResidence) return 'KNOWN';
    try {
      const savedStatus = localStorage.getItem(STORAGE_KEY_STATUS) as LocationStatus;
      const savedRes = localStorage.getItem(STORAGE_KEY_RESIDENCE);
      if (savedStatus) return savedStatus;
      return savedRes ? 'KNOWN' : 'NOT_PROVIDED';
    } catch {
      return initialCurrentResidence ? 'KNOWN' : 'NOT_PROVIDED';
    }
  });
  const [originRef, setOriginRef] = useState<DirectionReferencePoint>('CURRENT_RESIDENCE');
  const [customOrigin, setCustomOrigin] = useState<string>('');
  const [selectedHorizon, setSelectedHorizon] = useState<RelocationTimeHorizon>('1Y');
  const [activeTab, setActiveTab] = useState<'overview' | 'foreign' | 'continuum' | 'forensics'>('overview');

  // Search Popup inside Current Residence Space
  const [showResidencePopup, setShowResidencePopup] = useState(false);
  const [searchCityQuery, setSearchCityQuery] = useState('');
  const [citySearchResults, setCitySearchResults] = useState<VerifiedLocation[]>([]);
  const [isSearchingCity, setIsSearchingCity] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounced search for locations using canonical geocoding service
  useEffect(() => {
    if (!showResidencePopup) return;

    const clean = searchCityQuery.trim();
    if (clean.length < 2) {
      setCitySearchResults(CANONICAL_CITY_PRESETS.slice(0, 6));
      setIsSearchingCity(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchingCity(true);
      try {
        const results = await searchCanonicalLocations(clean);
        setCitySearchResults(results);
      } catch (err) {
        console.warn('[RelocationGeographyView] City search failed:', err);
      } finally {
        setIsSearchingCity(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchCityQuery, showResidencePopup]);

  // Focus search input when popup opens
  useEffect(() => {
    if (showResidencePopup) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    }
  }, [showResidencePopup]);

  // Compute 10-Dimension Relocation Event Signature with strict location isolation and horizon discovery
  const signature: RelocationEventSignature = useMemo(() => {
    return calculateRelocationEventSignature(kundali, lang, {
      focusDate,
      timeHorizon: selectedHorizon,
      originReference: originRef,
      currentResidence: confirmedCurrentResidence,
      currentResidenceStatus,
      customOrigin: customOrigin.trim() || undefined
    });
  }, [kundali, lang, focusDate, selectedHorizon, originRef, confirmedCurrentResidence, currentResidenceStatus, customOrigin]);

  const { foreignAssessment, humanComprehension, dualGocharSynthesis, acharyaForensics, locationProfile } = signature;

  const handleSelectLocation = (loc: VerifiedLocation | { placeName: string }) => {
    const chosenName = loc.placeName;
    setConfirmedCurrentResidence(chosenName);
    setCurrentResidenceStatus('KNOWN');
    setOriginRef('CURRENT_RESIDENCE');
    setShowResidencePopup(false);
    setSearchCityQuery('');
    try {
      localStorage.setItem(STORAGE_KEY_RESIDENCE, chosenName);
      localStorage.setItem(STORAGE_KEY_STATUS, 'KNOWN');
    } catch (e) {}
  };

  const handleSaveCustomText = () => {
    const clean = searchCityQuery.trim();
    if (clean.length > 0) {
      setConfirmedCurrentResidence(clean);
      setCurrentResidenceStatus('KNOWN');
      setOriginRef('CURRENT_RESIDENCE');
      setShowResidencePopup(false);
      setSearchCityQuery('');
      try {
        localStorage.setItem(STORAGE_KEY_RESIDENCE, clean);
        localStorage.setItem(STORAGE_KEY_STATUS, 'KNOWN');
      } catch (e) {}
    }
  };

  const handleClearCurrentResidence = () => {
    setConfirmedCurrentResidence(null);
    setCurrentResidenceStatus('NOT_PROVIDED');
    setShowResidencePopup(false);
    setSearchCityQuery('');
    try {
      localStorage.removeItem(STORAGE_KEY_RESIDENCE);
      localStorage.setItem(STORAGE_KEY_STATUS, 'NOT_PROVIDED');
    } catch (e) {}
  };

  const handleDeclineCurrentResidence = () => {
    setConfirmedCurrentResidence(null);
    setCurrentResidenceStatus('USER_DECLINED');
    setShowResidencePopup(false);
    setSearchCityQuery('');
    try {
      localStorage.removeItem(STORAGE_KEY_RESIDENCE);
      localStorage.setItem(STORAGE_KEY_STATUS, 'USER_DECLINED');
    } catch (e) {}
  };

  const handleConsultAcharya = () => {
    const prompt = `Please provide deep Vedic Acharya forensic guidance on my Relocation & Geography signature (${signature.eventType}), Direction: ${signature.directionAvailable ? signature.direction : 'Generalized (No Reference Origin)'} from ${signature.directionReferenceLabelEn}, Distance: ${signature.geographicScale}, and 12th House / Foreign indications under ${acharyaForensics.operatingDasha}.`;
    if (onOpenConsultation) {
      onOpenConsultation(prompt);
    }
  };

  return (
    <div className="space-y-6">
      {/* =================================================================== */}
      {/* 1. MASTER HEADER & DUAL GOCHAR CONVERGENCE BANNER                   */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-2xl p-5 sm:p-6 shadow-xs relative overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-astro-primary text-white text-xs font-bold uppercase tracking-wider">
                <Globe className="w-3.5 h-3.5 text-astro-accent" />
                {isHi ? 'स्थान परिवर्तन एवं भूगोल विश्लेषण' : 'Relocation & Geography Intelligence'}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-white text-astro-text text-xs font-medium border border-astro-border shadow-2xs">
                <Clock className="w-3 h-3 text-astro-accent" />
                {isHi ? 'स्थिति:' : 'Status:'} {signature.currentStatus}
              </span>

              {/* Explicit Direction Badge with Reference Guardrail */}
              {signature.directionAvailable ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-astro-secondary-surface text-astro-primary text-xs font-bold border border-astro-border">
                  <Compass className="w-3 h-3 text-astro-primary" />
                  {signature.direction} ({isHi ? 'दिशा' : 'Direction'})
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-900 text-xs font-medium border border-amber-200">
                  <HelpCircle className="w-3 h-3 text-amber-700" />
                  {isHi ? 'दिशा: संदर्भ बिंदु आवश्यक' : 'Direction: Reference Origin Required'}
                </span>
              )}
            </div>

            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-astro-primary font-serif tracking-tight">
              {signature.eventType}
            </h2>

            <p className="text-xs sm:text-sm text-astro-secondary-text max-w-3xl leading-relaxed">
              {isHi ? dualGocharSynthesis.descriptionHi : dualGocharSynthesis.descriptionEn}
            </p>

            <div className="pt-2 text-xs text-astro-primary font-mono bg-astro-secondary-surface/70 px-3 py-1.5 rounded-lg border border-astro-border inline-block">
              {acharyaForensics.shlokaSanskrit}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0">
            <button
              onClick={handleConsultAcharya}
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white font-bold text-xs shadow-xs transition-all cursor-pointer"
            >
              <Send className="w-3.5 h-3.5" />
              <span>{isHi ? 'आचार्य से स्थानांतरण संवाद' : 'Consult Acharya on Relocation'}</span>
            </button>
            <div className="text-center text-[10px] text-astro-muted">
              {isHi ? `फोकस दिनांक: ${signature.focusDate}` : `Focus Date: ${signature.focusDate}`}
            </div>
          </div>
        </div>
      </div>

      {/* =================================================================== */}
      {/* 2. STRICT LOCATION DATA MODEL & PROVENANCE BAR                      */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          {/* Box 1: Birth Place Record */}
          <div className="p-3 rounded-lg bg-astro-secondary-surface/50 border border-astro-border space-y-1">
            <div className="flex items-center justify-between text-astro-muted text-[10px] font-bold uppercase tracking-wider">
              <span>{isHi ? 'जन्मस्थान (Birth Place)' : 'Birth Place'}</span>
              <span className="text-[9px] px-1.5 py-0.2 rounded bg-white text-astro-secondary-text border border-astro-border">Birth Record</span>
            </div>
            <div className="font-bold text-astro-text text-sm flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-astro-accent shrink-0" />
              <span className="truncate">{kundali.birthDetails?.placeName || 'Unknown Birthplace'}</span>
            </div>
            <div className="text-[10px] text-astro-muted">
              {isHi ? 'केवल जन्म का भौतिक स्थान; वर्तमान निवास नहीं माना जा सकता' : 'Physical birthplace only; never inferred as residence'}
            </div>
          </div>

          {/* Box 2: Current Residence Record with Inline Small Popup Option */}
          <div className="relative">
            <div
              onClick={() => setShowResidencePopup(true)}
              className={`p-3 rounded-lg border space-y-1 cursor-pointer transition-all hover:shadow-xs group ${
                currentResidenceStatus === 'KNOWN' && confirmedCurrentResidence
                  ? 'bg-white border-astro-border hover:border-astro-primary'
                  : 'bg-amber-50/70 border-amber-200 hover:border-amber-400'
              }`}
            >
              <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider">
                <span className={currentResidenceStatus === 'KNOWN' ? 'text-astro-secondary-text' : 'text-amber-800'}>
                  {isHi ? 'वर्तमान निवास (Current Residence)' : 'Current Residence'}
                </span>
                <div className="flex items-center gap-1">
                  <span className={`text-[9px] px-1.5 py-0.2 rounded font-semibold ${
                    currentResidenceStatus === 'KNOWN'
                      ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-amber-100 text-amber-900 border border-amber-300'
                  }`}>
                    {currentResidenceStatus}
                  </span>
                  <span className="text-[10px] font-semibold text-astro-primary group-hover:underline flex items-center gap-0.5 ml-1">
                    <Edit2 className="w-2.5 h-2.5" />
                    {currentResidenceStatus === 'KNOWN' ? (isHi ? 'बदलें' : 'Change') : (isHi ? 'चुनें' : 'Set')}
                  </span>
                </div>
              </div>

              <div className="font-bold text-astro-text text-sm flex items-center gap-1.5">
                <Home className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                <span className="truncate">
                  {confirmedCurrentResidence || (
                    <span className="text-amber-800 font-medium italic">
                      {isHi ? 'वर्तमान नगर चुनें...' : 'Click to search current city...'}
                    </span>
                  )}
                </span>
              </div>

              <div className="text-[10px] text-astro-muted truncate">
                {currentResidenceStatus === 'KNOWN'
                  ? (isHi ? 'दिशा हेतु पुष्ट वर्तमान निवास (क्लिक कर बदलें)' : 'Confirmed residence origin (click to edit)')
                  : (isHi ? 'दिशा जानने हेतु क्लिक कर नगर चुनें' : 'Click to search & select current city')}
              </div>
            </div>

            {/* SMALL POPUP IN CURRENT RESIDENCE SPACE */}
            {showResidencePopup && (
              <>
                {/* Backdrop overlay for clean click-outside closing */}
                <div
                  className="fixed inset-0 z-40 bg-black/25 backdrop-blur-2xs transition-opacity"
                  onClick={() => setShowResidencePopup(false)}
                />

                <div className="absolute top-0 left-0 right-0 sm:left-auto sm:right-0 sm:w-96 z-50 bg-white border border-astro-border rounded-xl shadow-xl p-4 space-y-3 animate-in fade-in zoom-in-95 duration-150">
                  <div className="flex items-center justify-between border-b border-astro-border pb-2.5">
                    <div className="flex items-center gap-1.5 font-bold text-xs text-astro-primary">
                      <MapPin className="w-3.5 h-3.5 text-astro-accent" />
                      <span>{isHi ? 'वर्तमान निवास चुनें (Current Residence)' : 'Set Current Residence'}</span>
                    </div>
                    <button
                      onClick={() => setShowResidencePopup(false)}
                      className="text-astro-muted hover:text-astro-text p-1 rounded-md hover:bg-astro-secondary-surface transition-all cursor-pointer"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>

                  <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                    {isHi
                      ? 'आपका जन्मस्थान कभी भी वर्तमान निवास नहीं माना जाता। स्थानांतरण की दिशा जानने हेतु कृपया अपना वर्तमान नगर खोजें:'
                      : 'Birthplace is never assumed as current residence. To assess relocation direction, search your current city:'}
                  </p>

                  {/* Search Input Box */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-astro-muted absolute left-2.5 top-2.5" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchCityQuery}
                      onChange={e => setSearchCityQuery(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (citySearchResults.length > 0) {
                            handleSelectLocation(citySearchResults[0]);
                          } else {
                            handleSaveCustomText();
                          }
                        }
                      }}
                      placeholder={isHi ? 'नगर, राज्य अथवा देश खोजें...' : 'Search city, state or country...'}
                      className="w-full pl-8 pr-8 py-1.5 text-xs rounded-lg border border-astro-border bg-white text-astro-text focus:outline-hidden focus:ring-1 focus:ring-astro-primary"
                    />
                    {isSearchingCity ? (
                      <Loader2 className="w-3.5 h-3.5 text-astro-primary animate-spin absolute right-2.5 top-2.5" />
                    ) : searchCityQuery.length > 0 ? (
                      <button
                        onClick={() => setSearchCityQuery('')}
                        className="text-astro-muted hover:text-astro-text absolute right-2.5 top-2.5 cursor-pointer"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    ) : null}
                  </div>

                  {/* Live Search Results / Quick Suggestions */}
                  <div className="max-h-48 overflow-y-auto divide-y divide-astro-border/50 border border-astro-border/60 rounded-lg">
                    {citySearchResults.length > 0 ? (
                      citySearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectLocation(loc)}
                          className="w-full px-3 py-2 text-left hover:bg-astro-secondary-surface flex items-center justify-between text-xs transition-colors cursor-pointer group"
                        >
                          <div className="flex items-center gap-2 truncate">
                            <MapPin className="w-3 h-3 text-astro-muted group-hover:text-astro-primary shrink-0" />
                            <div className="truncate">
                              <span className="font-semibold text-astro-text">{loc.name}</span>
                              {loc.placeName && loc.placeName !== loc.name && (
                                <span className="text-[10px] text-astro-secondary-text ml-1.5">
                                  {loc.placeName.split(',').slice(1).join(',')}
                                </span>
                              )}
                            </div>
                          </div>
                          {confirmedCurrentResidence === loc.placeName && (
                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          )}
                        </button>
                      ))
                    ) : searchCityQuery.trim().length >= 2 ? (
                      <div className="p-3 text-center space-y-2">
                        <div className="text-[11px] text-astro-secondary-text">
                          {isHi ? 'कोई मेल नहीं मिला' : 'No canonical matches found.'}
                        </div>
                        <button
                          type="button"
                          onClick={handleSaveCustomText}
                          className="px-3 py-1 rounded bg-astro-secondary-surface text-astro-primary text-[11px] font-semibold hover:bg-astro-primary hover:text-white transition-all cursor-pointer"
                        >
                          {isHi ? `"${searchCityQuery}" को दर्ज करें` : `Use "${searchCityQuery}" as Custom Location`}
                        </button>
                      </div>
                    ) : (
                      <div className="p-2 text-center text-[10px] text-astro-muted">
                        {isHi ? 'प्रमुख नगर सुझाव' : 'Quick City Suggestions'}
                      </div>
                    )}
                  </div>

                  {/* Action Bar inside Popup */}
                  <div className="flex items-center justify-between pt-1 border-t border-astro-border text-[11px]">
                    {confirmedCurrentResidence ? (
                      <button
                        type="button"
                        onClick={handleClearCurrentResidence}
                        className="text-rose-700 hover:text-rose-900 font-semibold cursor-pointer"
                      >
                        {isHi ? 'वर्तमान निवास हटाएं' : 'Clear Residence'}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleDeclineCurrentResidence}
                        className="text-astro-muted hover:text-astro-text cursor-pointer"
                      >
                        {isHi ? 'अस्वीकार (सामान्य फलादेश)' : 'Decline (Keep Unknown)'}
                      </button>
                    )}

                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => {
                          setOriginRef('BIRTHPLACE');
                          setShowResidencePopup(false);
                        }}
                        className="px-2.5 py-1 rounded bg-astro-secondary-surface text-astro-text hover:bg-white border border-astro-border text-[10px] font-medium cursor-pointer"
                      >
                        {isHi ? 'जन्मस्थान संदर्भ' : 'Use Birthplace'}
                      </button>
                      {searchCityQuery.trim().length > 0 && (
                        <button
                          type="button"
                          onClick={handleSaveCustomText}
                          className="px-3 py-1 rounded bg-astro-primary text-white text-[10px] font-bold hover:bg-astro-secondary transition-all cursor-pointer"
                        >
                          {isHi ? 'सहेजें' : 'Save'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Box 3: Active Reference Location Record */}
          <div className="p-3 rounded-lg bg-astro-secondary-surface/50 border border-astro-border space-y-1">
            <div className="flex items-center justify-between text-astro-muted text-[10px] font-bold uppercase tracking-wider">
              <span>{isHi ? 'सक्रिय संदर्भ बिंदु (Reference)' : 'Active Reference'}</span>
              <span className="text-[9px] px-1.5 py-0.2 rounded bg-white text-astro-secondary-text border border-astro-border font-mono">
                {signature.directionReference}
              </span>
            </div>
            <div className="font-bold text-astro-text text-sm flex items-center gap-1.5">
              <Compass className="w-3.5 h-3.5 text-astro-primary shrink-0" />
              <span className="truncate">
                {locationProfile.referenceLocation?.locationValue || (isHi ? 'कोई संदर्भ स्थापित नहीं' : 'Not Established')}
              </span>
            </div>
            <div className="text-[10px] text-astro-muted truncate">
              {isHi ? signature.directionReferenceLabelHi : signature.directionReferenceLabelEn}
            </div>
          </div>
        </div>

        {/* ORIGIN REFERENCE SELECTOR BUTTONS */}
        <div className="pt-2 border-t border-astro-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sliders className="w-3.5 h-3.5 text-astro-accent" />
            <span className="text-xs font-bold text-astro-text">
              {isHi ? 'दिशा एवं दूरी का संदर्भ बिंदु:' : 'Select Direction Reference Origin:'}
            </span>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => {
                if (currentResidenceStatus !== 'KNOWN') {
                  setShowResidencePopup(true);
                } else {
                  setOriginRef('CURRENT_RESIDENCE');
                }
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                originRef === 'CURRENT_RESIDENCE'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'bg-astro-secondary-surface text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? 'वर्तमान निवास (Current Residence)' : 'Current Residence'}
              {confirmedCurrentResidence ? ` (${confirmedCurrentResidence})` : ''}
            </button>

            <button
              onClick={() => setOriginRef('BIRTHPLACE')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                originRef === 'BIRTHPLACE'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'bg-astro-secondary-surface text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? `जन्मस्थान (${kundali.birthDetails?.placeName || 'Birthplace'})` : `Birthplace (${kundali.birthDetails?.placeName || 'Birthplace'})`}
            </button>

            <button
              onClick={() => setOriginRef('USER_SELECTED')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                originRef === 'USER_SELECTED'
                  ? 'bg-astro-primary text-white shadow-2xs'
                  : 'bg-astro-secondary-surface text-astro-secondary-text hover:text-astro-text'
              }`}
            >
              {isHi ? 'अन्य चयनित नगर (Custom Origin)' : 'Custom Selected Origin'}
            </button>
          </div>
        </div>

        {originRef === 'USER_SELECTED' && (
          <div className="pt-2 border-t border-astro-border flex items-center gap-3">
            <span className="text-xs text-astro-secondary-text shrink-0">
              {isHi ? 'मूल स्थान का नाम दर्ज करें:' : 'Enter Custom Origin City/Country:'}
            </span>
            <input
              type="text"
              value={customOrigin}
              onChange={e => setCustomOrigin(e.target.value)}
              placeholder="e.g. New Delhi / Bengaluru / Dubai"
              className="px-3 py-1 text-xs rounded-lg border border-astro-border bg-white text-astro-text w-full max-w-xs focus:outline-hidden focus:ring-1 focus:ring-astro-primary"
            />
          </div>
        )}
      </div>

      {/* =================================================================== */}
      {/* 2.5 HORIZON SELECTOR (RG-HORIZON-EXT-V6.0)                         */}
      {/* =================================================================== */}
      <div className="bg-astro-card border border-astro-border rounded-xl p-3.5 shadow-2xs space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-xs font-bold text-astro-text">
            <Clock className="w-3.5 h-3.5 text-astro-accent" />
            <span>{isHi ? 'भविष्य खोज कालखंड (Discovery Horizon):' : 'Future Discovery Horizon:'}</span>
            <span className="text-[11px] font-normal text-astro-secondary-text">
              ({signature.focusDate} → {signature.horizonDiscovery?.horizonEndDate})
            </span>
          </div>
          <div className="text-[10px] text-astro-muted">
            {isHi ? 'फोकस दिनांक अपरिवर्तित रहता है; कालखंड भविष्य की घटनाओं को उजागर करता है' : 'Focus date remains fixed; horizon expands future discovery window'}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {(['7D', '30D', '90D', '6M', '1Y', '3Y', '5Y', '10Y'] as RelocationTimeHorizon[]).map(h => (
            <button
              key={h}
              onClick={() => setSelectedHorizon(h)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                selectedHorizon === h
                  ? 'bg-astro-primary text-white shadow-xs'
                  : 'bg-astro-secondary-surface text-astro-secondary-text hover:text-astro-text hover:bg-white border border-astro-border'
              }`}
            >
              {h}
            </button>
          ))}
        </div>
      </div>

      {/* =================================================================== */}
      {/* 3. FOUR INTERACTIVE SECTION TABS                                   */}
      {/* =================================================================== */}
      <div className="flex items-center p-1 rounded-xl bg-astro-secondary-surface border border-astro-border gap-1 overflow-x-auto">
        <button
          onClick={() => setActiveTab('overview')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
            activeTab === 'overview'
              ? 'bg-astro-primary text-white shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-text hover:bg-white'
          }`}
        >
          <Layers className="w-3.5 h-3.5" />
          <span>{isHi ? '1. ९-बिंदु स्पष्ट निष्कर्ष' : '1. 9-Point Relocation Verdict'}</span>
        </button>

        <button
          onClick={() => setActiveTab('foreign')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
            activeTab === 'foreign'
              ? 'bg-astro-primary text-white shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-text hover:bg-white'
          }`}
        >
          <Plane className="w-3.5 h-3.5" />
          <span>{isHi ? '2. विदेश व १२वां भाव विश्लेषण' : '2. Foreign & 12th House Analysis'}</span>
        </button>

        <button
          onClick={() => setActiveTab('continuum')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
            activeTab === 'continuum'
              ? 'bg-astro-primary text-white shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-text hover:bg-white'
          }`}
        >
          <Navigation className="w-3.5 h-3.5" />
          <span>{isHi ? '3. ४-चरणीय गतिशीलता चक्र' : '3. 4-Stage Movement Continuum'}</span>
        </button>

        <button
          onClick={() => setActiveTab('forensics')}
          className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold transition-all shrink-0 cursor-pointer ${
            activeTab === 'forensics'
              ? 'bg-astro-primary text-white shadow-xs'
              : 'text-astro-secondary-text hover:text-astro-text hover:bg-white'
          }`}
        >
          <BookOpen className="w-3.5 h-3.5" />
          <span>{isHi ? '4. शास्त्रीय साक्ष्य एवं डेटा मॉडल' : '4. Forensic Evidence & Provenance'}</span>
        </button>
      </div>

      {/* =================================================================== */}
      {/* TAB 1: HUMAN COMPREHENSION 9-POINT BREAKDOWN                       */}
      {/* =================================================================== */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          {signature.generalizedInterpretationOnly && (
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 flex items-start justify-between gap-3 text-xs text-astro-secondary-text">
              <div className="flex items-start gap-2.5">
                <Info className="w-4 h-4 text-astro-accent shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-astro-text">
                    {isHi ? 'सामान्य ज्योतिषीय स्थानांतरण फलादेश:' : 'Generalized Relocation Astrological Signature:'}
                  </span>{' '}
                  {isHi
                    ? 'वर्तमान निवास उपलब्ध न होने के कारण यह विश्लेषण सामान्य ज्योतिषीय काल-खंड एवं संभावना को दर्शाता है। दिशा जानने हेतु ऊपर वर्तमान निवास चुनें।'
                    : 'Because current residence is not provided, this analysis reflects generalized astrological timing and event activation. To view directional movement, set current residence above.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowResidencePopup(true)}
                className="shrink-0 px-3 py-1 rounded-lg bg-astro-primary text-white font-bold text-xs hover:bg-astro-secondary transition-all cursor-pointer"
              >
                {isHi ? 'वर्तमान निवास चुनें' : 'Set Residence'}
              </button>
            </div>
          )}

          {/* ================================================================= */}
          {/* HORIZON OUTLOOK SUMMARY & DISCOVERED WINDOWS TIMELINE (RG-EXT-V6) */}
          {/* ================================================================= */}
          {signature.horizonDiscovery && (
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-astro-accent shrink-0" />
                  <span className="text-xs font-bold text-astro-primary">
                    {isHi
                      ? `${signature.selectedHorizon} स्थानांतरण परिदृश्य (${signature.focusDate} से ${signature.horizonDiscovery.horizonEndDate})`
                      : `${signature.selectedHorizon} Relocation Outlook (${signature.focusDate} → ${signature.horizonDiscovery.horizonEndDate})`}
                  </span>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-astro-secondary-surface text-astro-primary border border-astro-border">
                  {signature.horizonDiscovery.totalEventsDiscovered} {isHi ? 'चिह्नित कालखंड' : 'Discovered Windows'}
                </span>
              </div>

              {/* Summary Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 text-center text-xs">
                <div className="p-2 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">{isHi ? 'कुल कालखंड' : 'Total Windows'}</div>
                  <div className="text-sm font-bold text-astro-text font-mono">{signature.horizonDiscovery.summary.totalWindows}</div>
                </div>
                <div className="p-2 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">{isHi ? 'करियर-प्रेरित' : 'Career-Linked'}</div>
                  <div className="text-sm font-bold text-blue-700 font-mono">{signature.horizonDiscovery.summary.careerLinked}</div>
                </div>
                <div className="p-2 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">{isHi ? 'केवल निवास' : 'Residence-Only'}</div>
                  <div className="text-sm font-bold text-teal-700 font-mono">{signature.horizonDiscovery.summary.residenceOnly}</div>
                </div>
                <div className="p-2 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">{isHi ? 'अंतरराष्ट्रीय' : 'International'}</div>
                  <div className="text-sm font-bold text-purple-700 font-mono">{signature.horizonDiscovery.summary.internationalCount}</div>
                </div>
                <div className="p-2 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">{isHi ? 'घरेलू' : 'Domestic'}</div>
                  <div className="text-sm font-bold text-emerald-700 font-mono">{signature.horizonDiscovery.summary.domesticCount}</div>
                </div>
                <div className="p-2 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">{isHi ? 'दिशा स्थापित' : 'Direction Active'}</div>
                  <div className="text-sm font-bold text-indigo-700 font-mono">{signature.horizonDiscovery.summary.directionEstablishedCount}</div>
                </div>
              </div>

              {/* No-Event State */}
              {!signature.horizonDiscovery.hasSignificantEvents && (
                <div className="p-4 rounded-lg bg-amber-50/70 border border-amber-200 text-xs text-amber-900 text-center font-medium">
                  {isHi ? signature.horizonDiscovery.noEventMessageHi : signature.horizonDiscovery.noEventMessageEn}
                </div>
              )}

              {/* Discovered Event Windows Timeline (Atīta ← Vartamāna → Āgāmi) */}
              {signature.horizonDiscovery.events.length > 0 && (
                <div className="space-y-2 pt-1">
                  <div className="text-[11px] font-bold text-astro-secondary-text uppercase tracking-wider">
                    {isHi ? 'कालक्रम प्रवाह (Timeline: अतीत ← वर्तमान → आगामी):' : 'Temporal Flow (Timeline: Atīta ← Vartamāna → Āgāmi):'}
                  </div>
                  <div className="space-y-2">
                    {signature.horizonDiscovery.events.map((evt, idx) => (
                      <div
                        key={evt.eventId}
                        className={`p-3 rounded-lg border space-y-1.5 transition-all ${
                          evt.lifecycle === 'ACTIVE'
                            ? 'bg-teal-50/50 border-teal-300 ring-1 ring-teal-400/30'
                            : 'bg-white border-astro-border hover:border-astro-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-astro-primary">#{idx + 1} {evt.eventType}</span>
                            <span className={`text-[9px] px-1.5 py-0.2 rounded font-bold uppercase ${
                              evt.lifecycle === 'ACTIVE'
                                ? 'bg-emerald-100 text-emerald-900 border border-emerald-300'
                                : 'bg-astro-secondary-surface text-astro-secondary-text border border-astro-border'
                            }`}>
                              {evt.lifecycle}
                            </span>
                            <span className="text-[9px] px-1.5 py-0.2 rounded bg-indigo-50 text-indigo-900 border border-indigo-200 font-semibold">
                              {evt.temporalPhase}
                            </span>
                          </div>

                          <div className="flex items-center gap-1.5 text-[10px]">
                            <span className="px-1.5 py-0.2 rounded bg-amber-50 text-amber-900 border border-amber-200">
                              {evt.precisionDisplayEn}
                            </span>
                            <span className="px-1.5 py-0.2 rounded bg-slate-50 text-slate-800 border border-slate-200">
                              BTR: {evt.btrSensitivity}
                            </span>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs pt-0.5">
                          <div>
                            <span className="text-astro-muted text-[10px] block">{isHi ? 'कालखंड:' : 'Window:'}</span>
                            <span className="font-semibold text-astro-text font-mono text-[11px]">
                              {evt.startDate} → {evt.endDate}
                            </span>
                          </div>
                          <div>
                            <span className="text-astro-muted text-[10px] block">{isHi ? 'अवधि एवं कारक:' : 'Duration & Driver:'}</span>
                            <span className="text-astro-secondary-text text-[11px]">
                              {evt.durationDisplay} ({evt.primaryDriver})
                            </span>
                          </div>
                          <div>
                            <span className="text-astro-muted text-[10px] block">{isHi ? 'दिशा एवं भूगोल:' : 'Direction & Scope:'}</span>
                            <span className="text-astro-secondary-text text-[11px]">
                              {evt.direction !== 'NOT_ESTABLISHED' ? `${evt.direction} · ` : ''}{evt.geographicClassification}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* WHAT */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-astro-secondary-text tracking-wider">{isHi ? '१. क्या? (WHAT)' : '1. WHAT? (Event)'}</span>
                <Home className="w-4 h-4 text-teal-600" />
              </div>
              <div className="text-sm font-bold text-astro-text">{isHi ? humanComprehension.whatHi : humanComprehension.whatEn}</div>
              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {signature.residenceChange === 'HIGH_LIKELIHOOD' ? (isHi ? 'निवास परिवर्तन की उच्च संभावना।' : 'High likelihood of physical home transition.') : (isHi ? 'मध्यम गतिशीलता योग।' : 'Moderate relocation indications.')}
              </p>
            </div>

            {/* WHY */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-astro-secondary-text tracking-wider">{isHi ? '२. क्यों? (WHY / Driver)' : '2. WHY? (Primary Driver)'}</span>
                <Briefcase className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-sm font-bold text-astro-text">{isHi ? humanComprehension.whyHi : humanComprehension.whyEn}</div>
              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {isHi ? `मुख्य कारक: ${signature.primaryDriver}` : `Principal life domain: ${signature.primaryDriver}`}
              </p>
            </div>

            {/* WHEN */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-astro-secondary-text tracking-wider">{isHi ? '३. कब? (WHEN / Timing)' : '3. WHEN? (Exact Date Window)'}</span>
                <Calendar className="w-4 h-4 text-amber-600" />
              </div>
              <div className="text-sm font-bold text-astro-text">{isHi ? humanComprehension.whenHi : humanComprehension.whenEn}</div>
              <p className="text-xs text-astro-secondary-text leading-relaxed font-mono">
                {isHi ? `शीर्ष प्रभाव: ${signature.peakDate}` : `Peak Resonance: ${signature.peakDate}`}
              </p>
              <div className="text-[10px] text-amber-900 bg-amber-50/80 px-2 py-1 rounded border border-amber-200">
                {isHi ? signature.peakDetails?.peakMethodologyHi : signature.peakDetails?.peakMethodologyEn}
              </div>
            </div>

            {/* WHERE */}
            <div className={`border rounded-xl p-4 shadow-2xs space-y-1.5 ${
              signature.directionAvailable ? 'bg-astro-card border-astro-border' : 'bg-amber-50/50 border-amber-200'
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-astro-secondary-text tracking-wider">{isHi ? '४. किस दिशा में? (WHERE)' : '4. WHERE? (Direction)'}</span>
                <Compass className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="text-sm font-bold text-astro-text">{isHi ? humanComprehension.whereHi : humanComprehension.whereEn}</div>
              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {isHi ? signature.directionReferenceLabelHi : signature.directionReferenceLabelEn}
              </p>
              {signature.directionAvailable && signature.directionProvenance && (
                <div className="text-[10px] text-indigo-900 bg-indigo-50/80 px-2 py-0.5 rounded border border-indigo-200">
                  {signature.directionProvenance.rashiElementBasis}
                </div>
              )}
            </div>

            {/* HOW FAR */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-astro-secondary-text tracking-wider">{isHi ? '५. कितनी दूरी? (HOW FAR)' : '5. HOW FAR? (Geographic Scale)'}</span>
                <Plane className="w-4 h-4 text-purple-600" />
              </div>
              <div className="text-sm font-bold text-astro-text">{isHi ? humanComprehension.howFarHi : humanComprehension.howFarEn}</div>
              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {isHi ? `पैमाना: ${signature.geographicScale}` : `Scale classification: ${signature.geographicScale}`}
              </p>
              <div className="text-[10px] text-purple-900 bg-purple-50/80 px-2 py-0.5 rounded border border-purple-200 truncate">
                {isHi ? signature.geographicContext?.jurisdictionExplanationHi : signature.geographicContext?.jurisdictionExplanationEn}
              </div>
            </div>

            {/* HOW LONG */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-astro-secondary-text tracking-wider">{isHi ? '६. कितने समय हेतु? (HOW LONG)' : '6. HOW LONG? (Duration)'}</span>
                <Clock className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-sm font-bold text-astro-text">{isHi ? humanComprehension.howLongHi : humanComprehension.howLongEn}</div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-xs text-astro-secondary-text font-medium">
                  {signature.durationDetails?.durationDays} {isHi ? 'सौर दिवस' : 'solar days'} ({signature.durationDetails?.durationMonths} {isHi ? 'माह' : 'months'})
                </span>
                {signature.isOpenEnded && (
                  <span className="text-[9px] px-1.5 py-0.2 rounded font-bold bg-teal-100 text-teal-900 border border-teal-300">
                    {isHi ? 'स्थायी अधिवास' : 'Open-Ended Domicile'}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-emerald-900 bg-emerald-50/80 px-2 py-0.5 rounded border border-emerald-200">
                {signature.durationDetails?.derivationMethodology}
              </div>
            </div>

            {/* FROM WHAT */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-astro-secondary-text tracking-wider">{isHi ? '७. किससे संबंध? (FROM WHAT)' : '7. FROM WHAT? (Birthplace Relation)'}</span>
                <RotateCcw className="w-4 h-4 text-cyan-600" />
              </div>
              <div className="text-sm font-bold text-astro-text">{isHi ? humanComprehension.fromWhatHi : humanComprehension.fromWhatEn}</div>
              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {isHi ? `संबंध: ${signature.birthplaceRelation}` : `Status: ${signature.birthplaceRelation}`}
              </p>
            </div>

            {/* HOW STRONG */}
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-astro-secondary-text tracking-wider">{isHi ? '८. साक्ष्य सामर्थ्य (HOW STRONG)' : '8. HOW STRONG? (Evidence)'}</span>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-sm font-bold text-astro-text">{isHi ? humanComprehension.howStrongHi : humanComprehension.howStrongEn}</div>
              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {isHi ? 'पाराशरी एवं जैमिनि वर्ग चक्रों का समग्र समर्थन।' : 'Convergent Parashari & Jaimini multi-tier corroboration.'}
              </p>
            </div>

            {/* WHAT IS UNCERTAIN */}
            <div className="bg-rose-50/60 border border-rose-200 rounded-xl p-4 shadow-2xs space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-rose-800 tracking-wider">{isHi ? '९. क्या अनिश्चित है? (UNCERTAIN)' : '9. WHAT IS UNCERTAIN?'}</span>
                <AlertTriangle className="w-4 h-4 text-rose-600" />
              </div>
              <div className="text-xs font-semibold text-rose-950">{isHi ? humanComprehension.whatIsUncertainHi : humanComprehension.whatIsUncertainEn}</div>
              <p className="text-[11px] text-rose-800 leading-relaxed">
                {isHi ? 'विशिष्ट देश अथवा नगर के नाम की ज्योतिषीय बाध्यता नहीं होती; यह गणना अनुकूल काल-खंड एवं संभावना को दर्शाती है।' : 'Specific destinations reflect personal karma and initiative; Jyotish calculates favorable temporal windows and axes.'}
              </p>
            </div>
          </div>

          {/* CAREER + RESIDENCE TEMPORAL OVERLAP BANNER */}
          {signature.careerResidenceOverlap && (
            <div className="bg-astro-card border border-astro-border rounded-xl p-4 shadow-2xs space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-astro-accent shrink-0" />
                  <span className="text-xs font-bold text-astro-primary">
                    {isHi ? 'आजीविका एवं निवास समय-संरेखण (Career + Residence Overlap)' : 'Career + Residence Temporal Overlap'}
                  </span>
                </div>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-astro-secondary-surface text-astro-primary border border-astro-border">
                  {signature.careerResidenceOverlap.overlapType}
                </span>
              </div>
              <p className="text-xs text-astro-secondary-text leading-relaxed">
                {isHi ? signature.careerResidenceOverlap.synthesisHi : signature.careerResidenceOverlap.synthesisEn}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 text-[11px]">
                <div className="p-2 rounded-lg bg-astro-secondary-surface/60 border border-astro-border/70">
                  <span className="font-bold text-astro-text">{isHi ? 'करियर कालखंड: ' : 'Career Window: '}</span>
                  <span className="text-astro-secondary-text">{isHi ? signature.careerResidenceOverlap.careerWindowHi : signature.careerResidenceOverlap.careerWindowEn}</span>
                </div>
                <div className="p-2 rounded-lg bg-astro-secondary-surface/60 border border-astro-border/70">
                  <span className="font-bold text-astro-text">{isHi ? 'निवास कालखंड: ' : 'Residence Window: '}</span>
                  <span className="text-astro-secondary-text">{isHi ? signature.careerResidenceOverlap.residenceWindowHi : signature.careerResidenceOverlap.residenceWindowEn}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* =================================================================== */}
      {/* TAB 2: FOREIGN / OVERSEAS QUAD-CLASSIFICATION                      */}
      {/* =================================================================== */}
      {activeTab === 'foreign' && (
        <div className="space-y-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 text-xs text-amber-950">
            <Shield className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-bold">
                {isHi ? 'कठोर शास्त्रीय नियम: द्वादश (१२वां) भाव ≠ स्वतः विदेश वास' : 'Hard Rule: 12th House != Automatic Foreign Country'}
              </span>
              <p className="leading-relaxed">
                {isHi ? foreignAssessment.twelfthHouseContextHi : foreignAssessment.twelfthHouseContextEn}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Travel */}
            <div className="bg-white border border-astro-border rounded-xl p-4 shadow-2xs space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-text">{isHi ? 'अल्पकालिक विदेश यात्रा' : 'Foreign Travel'}</span>
                <Plane className="w-4 h-4 text-blue-600" />
              </div>
              <div className="text-2xl font-bold text-astro-primary">{foreignAssessment.foreignTravelConfidence}%</div>
              <div className="text-xs text-astro-secondary-text">
                {foreignAssessment.foreignTravel ? (isHi ? 'प्रबल योग सक्रिय' : 'Supported by 9th/12th axis') : (isHi ? 'सीमित सक्रियण' : 'Limited activation')}
              </div>
            </div>

            {/* Stay */}
            <div className="bg-white border border-astro-border rounded-xl p-4 shadow-2xs space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-text">{isHi ? 'विस्तारित विदेश प्रवास' : 'Foreign Stay'}</span>
                <Briefcase className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="text-2xl font-bold text-astro-primary">{foreignAssessment.foreignStayConfidence}%</div>
              <div className="text-xs text-astro-secondary-text">
                {foreignAssessment.foreignStay ? (isHi ? 'मध्यम-अवधि कार्य प्रवास' : 'Medium-term work stay') : (isHi ? 'अल्पकालिक संभावना' : 'Low probability')}
              </div>
            </div>

            {/* Relocation */}
            <div className="bg-white border border-astro-border rounded-xl p-4 shadow-2xs space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-text">{isHi ? 'विदेश पुनर्वास' : 'Foreign Relocation'}</span>
                <Home className="w-4 h-4 text-teal-600" />
              </div>
              <div className="text-2xl font-bold text-astro-primary">{foreignAssessment.foreignRelocationConfidence}%</div>
              <div className="text-xs text-astro-secondary-text">
                {foreignAssessment.foreignRelocation ? (isHi ? 'दीर्घकालिक निवास परिवर्तन' : 'Sustained base change') : (isHi ? 'वर्तमान में अप्रमाणित' : 'Not firmly indicated')}
              </div>
            </div>

            {/* Settlement */}
            <div className="bg-white border border-astro-border rounded-xl p-4 shadow-2xs space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-astro-text">{isHi ? 'स्थायी विदेश नागरिकता' : 'Foreign Settlement'}</span>
                <Globe className="w-4 h-4 text-purple-600" />
              </div>
              <div className="text-2xl font-bold text-astro-primary">{foreignAssessment.foreignSettlementConfidence}%</div>
              <div className="text-xs text-astro-secondary-text">
                {foreignAssessment.foreignSettlement ? (isHi ? 'स्थायी अधिवास योग' : 'Permanent settlement viable') : (isHi ? 'पूर्ण वर्ग पुष्टि प्रतीक्षित' : 'Requires D4/D12 consensus')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* TAB 3: 4-STAGE MOVEMENT CONTINUUM                                  */}
      {/* =================================================================== */}
      {activeTab === 'continuum' && (
        <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-6">
          <div className="space-y-1">
            <h3 className="text-sm font-bold text-astro-text">
              {isHi ? '४-चरणीय गतिशीलता सातत्य (Movement Continuum)' : 'The 4-Stage Movement Continuum'}
            </h3>
            <p className="text-xs text-astro-secondary-text">
              {isHi
                ? 'शास्त्रीय गतिशीलता चार पृथक स्तरों में विकसित होती है: अल्पकालिक यात्रा से लेकर स्थायी अधिवास तक।'
                : 'Classical movement unfolds across 4 distinct thresholds. Travel does not automatically become permanent settlement.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 relative">
            {/* Step 1 */}
            <div className={`p-4 rounded-xl border ${signature.movementType === 'TRAVEL' ? 'bg-teal-50 border-teal-400 ring-2 ring-teal-500/20' : 'bg-white border-astro-border'} space-y-1.5`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-teal-800">STAGE 1</span>
                <Plane className="w-4 h-4 text-teal-600" />
              </div>
              <div className="text-xs font-bold text-astro-text">{isHi ? '१. अल्पकालिक यात्रा' : '1. Movement (Travel)'}</div>
              <p className="text-[11px] text-astro-secondary-text">{isHi ? 'तीर्थ, पर्यटन, अथवा संक्षिप्त कार्य हेतु गति।' : 'Temporary trips, exploration, and short journeys.'}</p>
            </div>

            {/* Step 2 */}
            <div className={`p-4 rounded-xl border ${signature.movementType === 'EXTENDED_STAY' ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-500/20' : 'bg-white border-astro-border'} space-y-1.5`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-indigo-800">STAGE 2</span>
                <Clock className="w-4 h-4 text-indigo-600" />
              </div>
              <div className="text-xs font-bold text-astro-text">{isHi ? '२. विस्तारित प्रवास' : '2. Extended Stay'}</div>
              <p className="text-[11px] text-astro-secondary-text">{isHi ? 'परियोजना अथवा शिक्षा हेतु ६-२४ माह का निवास।' : 'Project or study assignments (6–24 months).'}</p>
            </div>

            {/* Step 3 */}
            <div className={`p-4 rounded-xl border ${signature.movementType === 'RELOCATION' ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-500/20' : 'bg-white border-astro-border'} space-y-1.5`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-amber-800">STAGE 3</span>
                <Home className="w-4 h-4 text-amber-600" />
              </div>
              <div className="text-xs font-bold text-astro-text">{isHi ? '३. पुनर्वास (Relocation)' : '3. Relocation'}</div>
              <p className="text-[11px] text-astro-secondary-text">{isHi ? 'गृह परिवर्तन एवं नवीन आधार स्थापित करना।' : 'Physical shift of household and domestic base.'}</p>
            </div>

            {/* Step 4 */}
            <div className={`p-4 rounded-xl border ${signature.movementType === 'SETTLEMENT' ? 'bg-purple-50 border-purple-400 ring-2 ring-purple-500/20' : 'bg-white border-astro-border'} space-y-1.5`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold text-purple-800">STAGE 4</span>
                <Globe className="w-4 h-4 text-purple-600" />
              </div>
              <div className="text-xs font-bold text-astro-text">{isHi ? '४. स्थायी अधिवास' : '4. Settlement'}</div>
              <p className="text-[11px] text-astro-secondary-text">{isHi ? 'अचल संपत्ति व दीर्घकालिक स्थायी नागरिकता।' : 'Permanent property root and lifelong base.'}</p>
            </div>
          </div>
        </div>
      )}

      {/* =================================================================== */}
      {/* TAB 4: FORENSIC EVIDENCE & PROVENANCE                               */}
      {/* =================================================================== */}
      {activeTab === 'forensics' && (
        <div className="space-y-6">
          {/* LOCATION DATA MODEL PROVENANCE AUDIT */}
          <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-4">
            <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
              <Database className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'स्थान डेटा मॉडल एवं उद्गम लेखा-परीक्षण (Provenance Audit)' : 'Location Data Model & Provenance Audit'}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs font-mono">
              <div className="p-3 rounded-lg bg-white border border-astro-border space-y-1">
                <div className="text-astro-muted text-[10px] uppercase font-bold">1. Birth Place Record</div>
                <div><span className="text-astro-secondary-text">Type:</span> {locationProfile.birthPlace.locationType}</div>
                <div><span className="text-astro-secondary-text">Value:</span> {locationProfile.birthPlace.locationValue}</div>
                <div><span className="text-astro-secondary-text">Source:</span> {locationProfile.birthPlace.source}</div>
                <div><span className="text-astro-secondary-text">Confirmed:</span> {String(locationProfile.birthPlace.confirmedByUser)}</div>
              </div>

              <div className="p-3 rounded-lg bg-white border border-astro-border space-y-1">
                <div className="text-astro-muted text-[10px] uppercase font-bold">2. Current Residence Record</div>
                <div><span className="text-astro-secondary-text">Status:</span> {locationProfile.currentResidenceStatus}</div>
                <div><span className="text-astro-secondary-text">Value:</span> {locationProfile.currentResidence?.locationValue || 'null'}</div>
                <div><span className="text-astro-secondary-text">Source:</span> {locationProfile.currentResidence?.source || 'NONE'}</div>
                <div><span className="text-astro-secondary-text">Inferred from Birthplace:</span> <span className="font-bold text-emerald-700">STRICTLY FALSE</span></div>
              </div>

              <div className="p-3 rounded-lg bg-white border border-astro-border space-y-1">
                <div className="text-astro-muted text-[10px] uppercase font-bold">3. Active Reference Point</div>
                <div><span className="text-astro-secondary-text">Reference Type:</span> {signature.directionReference}</div>
                <div><span className="text-astro-secondary-text">Resolved Value:</span> {locationProfile.referenceLocation?.locationValue || 'NONE'}</div>
                <div><span className="text-astro-secondary-text">Direction Available:</span> {String(signature.directionAvailable)}</div>
              </div>

              <div className="p-3 rounded-lg bg-white border border-astro-border space-y-1">
                <div className="text-astro-muted text-[10px] uppercase font-bold">4. Cache & AI Gateway Contract</div>
                <div className="truncate"><span className="text-astro-secondary-text">Cache Key:</span> {signature.cacheKey}</div>
                <div><span className="text-astro-secondary-text">Natal Only:</span> {String(signature.aiGatewayPayload.natalEventSignatureOnly)}</div>
                <div><span className="text-astro-secondary-text">Prohibited Claims:</span> {signature.aiGatewayPayload.prohibitedClaims.length} guardrails</div>
              </div>
            </div>
          </div>

          {/* JYOTISH FORENSICS */}
          <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-4">
            <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
              <BookOpen className="w-4 h-4 text-astro-accent" />
              <span>{isHi ? 'आचार्य शास्त्रीय साक्ष्य एवं भावेश विश्लेषण' : 'Acharya Bhavesh & Transit Forensics'}</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {acharyaForensics.bhaveshMatrix.map((item, idx) => (
                <div key={idx} className="p-3 rounded-lg bg-white border border-astro-border space-y-1">
                  <div className="text-astro-secondary-text text-[11px]">House {item.house} Lord: <span className="font-bold text-astro-text">{item.lord}</span></div>
                  <div className="font-semibold text-astro-text">Placed in House {item.placementHouse}</div>
                  <div className="text-[10px] text-teal-700">{item.strength}</div>
                </div>
              ))}
            </div>

            <div className="p-4 rounded-xl bg-astro-secondary-surface border border-astro-border space-y-2">
              <div className="text-xs font-bold text-astro-text">
                {isHi ? 'शास्त्रीय पाराशरी प्रमाण:' : 'Classical Parashari Shloka Citation:'}
              </div>
              <p className="text-xs text-astro-secondary-text italic leading-relaxed">
                "{isHi ? acharyaForensics.classicalCitationHi : acharyaForensics.classicalCitationEn}"
              </p>
            </div>
          </div>

          {/* D-4 CHATURTHAMSHA DIVISIONAL ANALYSIS */}
          {signature.d4Analysis && (
            <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
                  <Home className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'चतुर्थांश (D-4) वर्ग चक्र विश्लेषण' : 'D-4 Chaturthamsha (Fixed Assets & Domicile) Analysis'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                    signature.d4Analysis.supportive
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                      : 'bg-amber-50 text-amber-800 border-amber-200'
                  }`}>
                    {signature.d4Analysis.mobilityNature} ({signature.d4Analysis.d4MobilityScore}/100)
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold">D-4 LAGNA</div>
                  <div className="font-semibold text-astro-text">{signature.d4Analysis.d4LagnaSign}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold">D-4 4TH HOUSE</div>
                  <div className="font-semibold text-astro-text">{signature.d4Analysis.d4FourthHouseSign}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold">D-4 4TH LORD</div>
                  <div className="font-semibold text-astro-text">{signature.d4Analysis.d4FourthLord}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold">4TH LORD PLACEMENT</div>
                  <div className="font-semibold text-astro-text">House {signature.d4Analysis.d4FourthLordPlacementHouse} ({signature.d4Analysis.d4FourthLordPlacementSign})</div>
                </div>
              </div>

              <p className="text-xs text-astro-secondary-text leading-relaxed bg-astro-secondary-surface/60 p-3 rounded-lg border border-astro-border/70">
                {isHi ? signature.d4Analysis.classicalTextBasisHi : signature.d4Analysis.classicalTextBasisEn}
              </p>
            </div>
          )}

          {/* 5-PART WEIGHTED EVIDENCE MATRIX */}
          {signature.evidenceBreakdown && signature.evidenceBreakdown.length > 0 && (
            <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  <span>{isHi ? '५-चरणीय पारदर्शी साक्ष्य मैट्रिक्स (Evidence Matrix)' : '5-Tier Transparent Weighted Evidence Matrix'}</span>
                </div>
                <span className="text-xs font-bold text-astro-primary font-mono">
                  {signature.confidenceScore}% {isHi ? 'सहमति' : 'Convergence'} ({signature.evidenceStrength})
                </span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border border-astro-border rounded-lg overflow-hidden">
                  <thead className="bg-astro-secondary-surface text-astro-muted text-[10px] uppercase font-bold">
                    <tr>
                      <th className="p-2.5">Dimension / Factor</th>
                      <th className="p-2.5 text-center">Weight</th>
                      <th className="p-2.5 text-center">Raw Score</th>
                      <th className="p-2.5 text-center">Weighted</th>
                      <th className="p-2.5">Classical Parashari / Jaimini Basis</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-astro-border/60 bg-white">
                    {signature.evidenceBreakdown.map((item, idx) => (
                      <tr key={idx} className="hover:bg-astro-secondary-surface/40 transition-colors">
                        <td className="p-2.5 font-bold text-astro-text">{item.dimension}</td>
                        <td className="p-2.5 text-center font-mono text-astro-secondary-text">{item.weight}%</td>
                        <td className="p-2.5 text-center font-mono font-semibold text-astro-text">{item.rawScore}/100</td>
                        <td className="p-2.5 text-center font-mono font-bold text-teal-800">+{item.weightedScore}%</td>
                        <td className="p-2.5 text-astro-secondary-text leading-tight text-[11px]">
                          {isHi ? item.classicalBasisHi : item.classicalBasisEn}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* DIRECTIONAL PROVENANCE & SANSKRIT ATTRIBUTION */}
          {signature.directionProvenance && (
            <div className="bg-astro-card border border-astro-border rounded-xl p-5 shadow-2xs space-y-3">
              <div className="flex items-center gap-2 font-bold text-sm text-astro-text">
                <Compass className="w-4 h-4 text-indigo-600" />
                <span>{isHi ? 'दिशा निर्धारण एवं शास्त्रीय उद्गम (Direction Provenance)' : 'Directional Methodology & Sanskrit Provenance'}</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs font-mono">
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">Direction & Origin</div>
                  <div className="font-bold text-indigo-900">{signature.directionProvenance.direction}</div>
                  <div className="text-[10px] text-astro-secondary-text truncate">{signature.directionProvenance.referenceOriginLabel}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">12th Sign Element Basis</div>
                  <div className="text-astro-text truncate">{signature.directionProvenance.rashiElementBasis}</div>
                </div>
                <div className="p-2.5 rounded-lg bg-white border border-astro-border">
                  <div className="text-[10px] text-astro-muted font-bold uppercase">9th Lord Digbala Basis</div>
                  <div className="text-astro-text truncate">{signature.directionProvenance.grahaDigbalaBasis}</div>
                </div>
              </div>

              <div className="text-xs text-astro-secondary-text italic bg-indigo-50/50 p-3 rounded-lg border border-indigo-100 leading-relaxed">
                "{isHi ? signature.directionProvenance.classicalSourceHi : signature.directionProvenance.classicalSourceEn}"
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
